import { useState } from 'react';
import {
  answersComplete, askingRows, fill, siteTypeOfferPath, t,
  type SiteTypeDefaultAnswer, type SiteTypeDefaultAnswers, type SiteTypeDefaultSetting, type SiteTypeOfferAnswer, type StringKey,
} from '@bms2/shared';
import { RadioGroup } from '../../components/controls/Radio';
import { FormFields, FormFrame } from '../../components/forms/FormFrame';
import { get } from './api';
import styles from './SiteTypeAnswerWindow.module.css';

const SETTING_KEYS: Readonly<Record<SiteTypeDefaultSetting, StringKey>> = {
  requirements: 'siteTypeAnswer.requirements',
  messages: 'siteTypeAnswer.messages',
  taxExempt: 'siteTypeAnswer.taxExempt',
};

/** A side of a row in words: its lines, or None; the flag's Yes and No through the string table. */
function sideWords(setting: SiteTypeDefaultSetting, lines: readonly string[]): string {
  if (lines.length === 0) return t('siteTypeAnswer.nothing');
  if (setting === 'taxExempt') return lines[0] === 'Yes' ? t('siteTypeAnswer.yes') : t('siteTypeAnswer.no');
  return lines.join(', ');
}

/** 🔒 T3522: the Work order messages would be filled and this person may not write them, so the Save passes them over. */
export function messagesPassedOver(offer: SiteTypeOfferAnswer): boolean {
  return offer.rows.some((row) => row.setting === 'messages' && row.state === 'fill' && row.blocked !== null);
}

/** The type a Save chooses for a site: a type named and different from the one the site carries, or null. */
export function siteTypeChosen(held: unknown, sent: unknown): string | null {
  return typeof sent === 'string' && sent !== '' && sent !== held ? sent : null;
}

/**
 * 🔒 The Site's Edit window asks before a type's defaults overwrite anything set (piece 13; decision 10210; T3514). `check`
 * reads what the chosen type offers: it opens the window where a set setting differs and answers `ask`, answers a
 * refusal where the read fails, and null where nothing asks, so the Save goes on and the server fills what was never set.
 */
export function useSiteTypeAsk(apiBaseUrl: string) {
  const [offer, setOffer] = useState<SiteTypeOfferAnswer | null>(null);
  const check = async (siteId: string, siteTypeId: string, taxExempt?: boolean): Promise<'ask' | string | null> => {
    const read = await get<SiteTypeOfferAnswer>(siteTypeOfferPath(siteId, siteTypeId, taxExempt), apiBaseUrl);
    if (!read.ok) return `${t('siteTypeAnswer.readFailed')} ${read.message}`;
    // 🔒 T3522: the window also opens to say the messages are passed over, so a fill that skips them is never silent.
    if (askingRows(read.data).length === 0 && !messagesPassedOver(read.data)) return null;
    setOffer(read.data);
    return 'ask';
  };
  return { offer, check, close: () => setOffer(null) };
}

/**
 * 🔒 Apply Site Type Defaults (decision 10210): one row per setting the site has set that the type would change, with
 * what the site has now, what the type offers, and Overwrite or Keep. 🔨 Apply stays grayed, saying why, until every row
 * has an answer; Overwrite is grayed with the server's sentence where this person may not change that setting. Cancel
 * returns to the Edit form with nothing saved. Drawn through the Form frame (decision 3060).
 */
export function SiteTypeAnswerWindow({ offer, onCancel, onApply }: { offer: SiteTypeOfferAnswer; onCancel: () => void; onApply: (answers: SiteTypeDefaultAnswers) => void }) {
  const [answers, setAnswers] = useState<SiteTypeDefaultAnswers>({});
  const rows = askingRows(offer);
  const complete = answersComplete(rows, answers);
  const answer = (setting: SiteTypeDefaultSetting, value: string) => setAnswers((was) => ({ ...was, [setting]: value as SiteTypeDefaultAnswer }));
  return (
    <FormFrame
      host="window"
      open
      title={t('siteTypeAnswer.title')}
      dataForm="site-type-answer"
      typed={Object.keys(answers).length > 0}
      onCancel={onCancel}
      act={{ label: t('siteTypeAnswer.apply'), onAct: () => onApply(answers), disabled: !complete, disabledReason: t('siteTypeAnswer.unanswered') }}
    >
      <FormFields>
        <p className={styles.hint}>{fill(t('siteTypeAnswer.hint'), { type: offer.typeName })}</p>
        <p className={styles.hint}>{t('siteTypeAnswer.filled')}</p>
        {messagesPassedOver(offer) ? <p className={styles.hint} data-site-type-answer-skipped="messages">{t('siteTypeAnswer.messagesSkipped')}</p> : null}
        <ul className={styles.rows}>
          {rows.map((row) => (
            <li key={row.setting} className={styles.row} data-site-type-answer={row.setting}>
              <p className={styles.setting}>{t(SETTING_KEYS[row.setting])}</p>
              <dl className={styles.sides}>
                <dt>{row.setting === 'messages' ? t('siteTypeAnswer.customerNow') : t('siteTypeAnswer.now')}</dt>
                <dd data-side="now">{sideWords(row.setting, row.now)}</dd>
                <dt>{t('siteTypeAnswer.offered')}</dt>
                <dd data-side="offered">{sideWords(row.setting, row.offered)}</dd>
              </dl>
              <RadioGroup
                name={`site-type-answer-${row.setting}`}
                legend={t(SETTING_KEYS[row.setting])}
                legendHidden
                orientation="horizontal"
                value={answers[row.setting] ?? null}
                onChange={(value) => answer(row.setting, value)}
                options={[
                  { value: 'overwrite', label: t('siteTypeAnswer.overwrite'), disabled: row.blocked !== null },
                  { value: 'keep', label: t('siteTypeAnswer.keep') },
                ]}
              />
              {row.setting === 'messages' ? <p className={styles.hint} data-site-type-answer-customer="messages">{t('siteTypeAnswer.messagesCustomer')}</p> : null}
              {row.blocked ? <p className={styles.hint} data-site-type-answer-blocked={row.setting}>{row.blocked}</p> : null}
            </li>
          ))}
        </ul>
      </FormFields>
    </FormFrame>
  );
}
